import type { PayloadAction } from "@reduxjs/toolkit";
import { INITIAL_META } from "src/components/VendorPortal/ListingLayout/shared/constants";
import type { ListingTableMap } from "src/components/VendorPortal/ListingLayout/shared/types";
import { type AppDate, dayjs } from "src/helper/date";
import type {
  FilterMap,
  GenericObj,
  MediaType,
  OptionType,
  SortMap,
  ValueOf,
} from "src/helper/shared-types";
import {
  checkAllIssuesMarked,
  getWorkOrderStatus,
  isQuoteRejected,
  updateWorkOrderIssue,
  updateWorkOrderIssueStatus,
} from "src/helper/work-order-utils";
import {
  CANCELLATION_WO_STEPS,
  QUALITY_CHECK_STATUS,
  WO_DETAILS_TABS_KEYS,
  WO_SORT_DEFAULT,
  WO_STATUS_KEYS,
  WO_TAB_FILTERS,
} from "src/views/VendorPortal/WorkOrder/constants";
import {
  WO_CLOSE_WORK_ORDER_TABS_KEYS,
  type WOCloseWorkOrderTabs,
} from "src/views/VendorPortal/WorkOrder/SidePanel/CloseWorkOrder/constants";
import type {
  QuoteTaskFormType,
  QuoteTaskOptionFormType,
} from "src/views/VendorPortal/WorkOrder/SidePanel/QuoteTaskDetails/types";
import { buildIssueDetailsPanelSnapshot } from "./helpers";
import type {
  CloseWorkOrderFormType,
  CloseWorkOrderSidePanelType,
  MaterialType,
  QuoteTaskResponseType,
  QuoteTaskType,
  QuoteType,
  RescheduleSidePanelType,
  StatusType,
  TaskOptionType,
  TaskType,
  TripType,
  WODetailsSidePanelType,
  WODetailsTabs,
  WorkOrderDataType,
  WorkOrderIssueType,
  WorkOrderListingType,
  WorkOrderStateType,
} from "./types";

const reducers = {
  changeWorkOrderTab: (
    state: WorkOrderStateType,
    action: PayloadAction<string>,
  ) => {
    if (state.currentListingTab !== action.payload) {
      state.currentListingTab = action.payload;
      state.isFilteringList = true;
      state.isFilterLoading = true;
      state.filters = {
        status: WO_TAB_FILTERS[action.payload],
        category: [""],
        closed_date: [""],
        paused_date: [""],
        scheduled_date: [""],
      };
      state.search.searchInput = "";
      state.filtersCount = 0;
      state.meta = INITIAL_META;
      state.sortBy = WO_SORT_DEFAULT[action.payload];
    }
  },
  clearWorkOrderListFilters: (state: WorkOrderStateType) => {
    state.filters = {
      status: [`all_${state.currentListingTab}`],
      category: [""],
      closed_date: [""],
      paused_date: [""],
      scheduled_date: [""],
    };

    state.isFilteringList = true;
    state.meta = INITIAL_META;
    state.filtersCount = 0;
    state.search.searchInput = "";
  },
  filterWorkOrderList: (
    state: GenericObj & WorkOrderStateType,
    action: PayloadAction<{
      key: string;
      count: number;
      value: string[];
    }>,
  ) => {
    const { key, count, value } = action.payload;

    if (state.filters[key] !== value) {
      state.filtersCount = count;
      state.isFilteringList = true;
      state.filters = {
        ...state.filters,
        [key]: value,
      };
      state.meta = INITIAL_META;
    }
  },
  searchWorkOrderList: (
    state: WorkOrderStateType,
    action: PayloadAction<string>,
  ) => {
    if (state.search.searchInput !== action.payload) {
      state.isFilteringList = true;
      state.meta = INITIAL_META;
      state.search.searchInput = action.payload;
    }
  },
  setWOFilterMapping: (
    state: WorkOrderStateType,
    action: PayloadAction<FilterMap[]>,
  ) => {
    state.filterMapping = action.payload;
  },
  setWOSortMapping: (
    state: WorkOrderStateType,
    action: PayloadAction<SortMap[]>,
  ) => {
    state.sortMapping = action.payload;
  },
  setWOTableMapping: (
    state: WorkOrderStateType,
    action: PayloadAction<ListingTableMap[]>,
  ) => {
    state.tableMapping = action.payload;
  },
  // setListingController: (
  //   state: WorkOrderStateType,
  //   action: PayloadAction<AbortController>,
  // ) => {
  //   state.listingController = action.payload;
  // },
  sortWorkOrderList: (
    state: WorkOrderStateType,
    action: PayloadAction<string>,
  ) => {
    // Only set loading state if the value actually changed
    if (state.sortBy !== action.payload) {
      state.isFilteringList = true;
    }
    state.sortBy = action.payload;
    state.meta = INITIAL_META;
  },
  updateWorkOrderList: <K extends keyof WorkOrderStateType["source"]>(
    state: WorkOrderStateType,
    action: PayloadAction<{
      workOrderId: string;
      data: Partial<WorkOrderDataType[K]>;
      key: K;
    }>,
  ) => {
    const index: number = state.data.findIndex(
      (wo: WorkOrderListingType) =>
        wo.work_order_id === action.payload.workOrderId,
    );
    if (index !== -1) {
      state.data[index] = {
        ...state.data[index],
        [action.payload.key]: action.payload.data,
      };
    }
  },
  updateWorkOrderDetails: <K extends keyof WorkOrderStateType["source"]>(
    state: WorkOrderStateType,
    action: PayloadAction<{
      workOrderId: string;
      data: Partial<WorkOrderDataType[K]>;
      key: K;
      issue_id?: string; // Optional issue_id for nested updates
    }>,
  ) => {
    // Special handling for adding quotes to specific work order issues
    if (
      action.payload.key === "work_order_issues" &&
      action.payload.issue_id &&
      "quotes" in action.payload.data
    ) {
      const issueId = action.payload.issue_id;
      const quotesData = (action.payload.data as any).quotes;

      state.source.work_order_issues = state.source.work_order_issues?.map(
        (issue) => {
          if (issue.issue_id === issueId) {
            return {
              ...issue,
              quotes: issue.quotes
                ? [...issue.quotes, ...quotesData]
                : quotesData,
            };
          }
          return issue;
        },
      );
    } else {
      // Normal update for other cases
      state.source = {
        ...state.source,
        [action.payload.key]: action.payload.data,
      };
      // Update listing data
      const index: number = state.data.findIndex(
        (wo: WorkOrderListingType) =>
          wo.work_order_id === action.payload.workOrderId,
      );
      if (index !== -1) {
        state.data[index] = {
          ...state.data[index],
          [action.payload.key]: action.payload.data,
        };
      }
    }
  },
  updateWorkOrderTrip: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      tripId: string;
      data: Partial<TripType>;
    }>,
  ) => {
    const index: number = state.source.trips.findIndex(
      (trip: TripType) => trip.trip_id === action.payload.tripId,
    );
    const updatedTrip: TripType = {
      ...state.source.trips[index],
      ...action.payload.data,
    };
    state.source.trips[index] = updatedTrip;
  },
  updateWorkOrderQuotes: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      quoteId: string;
      data: QuoteType;
    }>,
  ) => {
    if (!state.source.quotes) {
      state.source.quotes = [];
    }
    if (!state.source.work_order_issues) {
      state.source.work_order_issues = [];
    }
    const index: number = state.source.quotes
      ? state.source.quotes.findIndex(
          (quote: QuoteType) => quote.quote_id === action.payload.quoteId,
        )
      : -1;
    // ORI-617: a quote can span MULTIPLE work-order issues — the same quote_id appears
    // under every issue it quoted (see getWorkOrderStatus's ORI-614 comment, and the
    // backend WorkOrderIssueResource, which emits a QuoteResource per issue for a
    // shared quote instance). getWorkOrderStatus below evaluates the rejection lock-out
    // across ALL issues, so every copy of the quote must be updated — not just the
    // first — or a sibling issue's stale copy keeps a multi-issue rejection out of
    // Completed.
    let matchedAnyIssue = false;
    for (const issue of state.source.work_order_issues ?? []) {
      const issueQuotes = issue.quotes;
      if (!issueQuotes) {
        continue;
      }
      const quoteIndex: number = issueQuotes.findIndex(
        (quote: QuoteType) => quote.quote_id === action.payload.quoteId,
      );
      if (quoteIndex !== -1) {
        matchedAnyIssue = true;
        issueQuotes[quoteIndex] = {
          ...issueQuotes[quoteIndex],
          ...action.payload.data,
        };
      }
    }
    if (index === -1) {
      // The quote isn't in source.quotes yet — reconcile via a refetch rather than
      // guessing (a phantom-append would render a task-less summary card).
      state.sidePanels.details = {
        ...state.sidePanels.details,
        needToRefetch: true,
      };
      return;
    }
    const updatedQuote: QuoteType = {
      ...state.source.quotes[index],
      ...action.payload.data,
    };
    state.source.quotes[index] = updatedQuote;
    // ORI-617: the quote is in source.quotes but was under NO issue, so the
    // issue-driven derivation below cannot observe this rejection. Schedule a panel
    // refetch to reconcile — the rejection branch otherwise refreshes only the list,
    // not the open panel, and rejection frames never set needToRefetch on their own.
    if (!matchedAnyIssue && isQuoteRejected(action.payload.data)) {
      state.sidePanels.details = {
        ...state.sidePanels.details,
        needToRefetch: true,
      };
    }
    // ORI-617: re-derive the open work order's status through the single source of
    // truth (getWorkOrderStatus + its ORI-614 hasOnlyRejectedQuotes guard) instead of
    // letting the Pusher handler hard-write it, so the rejected-with-live-replacement
    // case is decided in one place. getWorkOrderStatus reads work_order_issues (every
    // copy updated above). Only a PAUSED work order — the awaiting-a-quote-decision
    // state this fix is about — transforms here, so guard on PAUSED and pass no trips:
    // the Paused branch never reads trips, and the work_in_progress branch (which does,
    // reachable when workOrder.updated wrote a raw status) can never be legitimately
    // changed by a quote frame. Guarding on PAUSED also keeps a not-yet-loaded / raw
    // status object from throwing in getWorkOrderStatus, which reads status.value
    // immediately.
    if (state.source.status?.value === WO_STATUS_KEYS.PAUSED) {
      state.source.status = getWorkOrderStatus(
        state.source.status,
        undefined,
        state.source.work_order_issues,
      );
    }
  },
  updateVendorSidePanel: <K extends keyof WorkOrderStateType["sidePanels"]>(
    state: WorkOrderStateType,
    action: PayloadAction<{
      key: K;
      data: Partial<WorkOrderStateType["sidePanels"][K]>;
    }>,
  ) => {
    state.sidePanels[action.payload.key] = {
      ...state.sidePanels[action.payload.key],
      ...action.payload.data,
    };
  },
  setWODetailsSidePanelLoading: (
    state: WorkOrderStateType,
    action: PayloadAction<boolean>,
  ) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: action.payload,
    };
  },
  openWODetailsSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WODetailsSidePanelType["workOrderUuid"]>,
  ) => {
    // ORI-465: a hardware Back that dismisses a nested sub-panel re-fires the
    // ?woId popstate listener (VendorWorkOrderDetailsPanel), which re-dispatches
    // this open for the SAME work order. Re-opening an already-open panel must
    // be a no-op — flipping isLoading would flash a skeleton over the
    // still-visible panel and the state.source reset below would clobber the
    // loaded work order for nothing.
    if (
      state.sidePanels.details.isOpen &&
      state.sidePanels.details.workOrderUuid === action.payload
    ) {
      return;
    }
    state.source = {
      trips: [],
      quotes: [],
      work_order_issues: [],
    } as unknown as WorkOrderDataType;
    state.sidePanels.details = {
      ...state.sidePanels.details,
      currentTab: WO_DETAILS_TABS_KEYS.DETAILS,
      isClosingWO: false,
      isOpen: true,
      isLoading: true,
      workOrderUuid: action.payload,
      // ORI-627: clear the previous work order's failure before the new fetch starts.
      // The hook's dispatch lands after this reducer, so without clearing here the
      // error branch would render for one committed frame over a freshly-opened panel.
      hasDetailsError: false,
    };
  },
  closeWODetailsSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      currentTab: WO_DETAILS_TABS_KEYS.DETAILS,
      isOpen: false,
      workOrderUuid: "",
      // ORI-627: a closed panel has no failure to report; leaving the flag set would
      // resurface the error banner the next time this panel opens.
      hasDetailsError: false,
    };
    // ORI-465 cascade-close: tearing down the parent WO detail panel also
    // resets every nested sub-panel, so a Back past the parent can never orphan
    // a visible nested panel over a closed parent. Pure state reset — history
    // reconciliation is owned by useHistoryDismiss in the components. WO nested
    // panels can stack (e.g. QuoteApprove → IssueDetails), so all slots are
    // reset unconditionally. `state.source` is intentionally left untouched:
    // no nested-panel snapshot survives close (there is no WO equivalent of the
    // request ORI-420 claim snapshot).
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.reschedule = {
      ...state.sidePanels.reschedule,
      currentStep: undefined,
      reason: null,
      isOpen: false,
    };
    state.sidePanels.closeWorkOrder = {
      ...state.sidePanels.closeWorkOrder,
      currentTab: WO_CLOSE_WORK_ORDER_TABS_KEYS.SUMMARY,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.cancelWO = {
      ...state.sidePanels.cancelWO,
      steps: { key: "", step: CANCELLATION_WO_STEPS.STEP_1 },
      isOpen: false,
    };
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      data: undefined,
      isOpen: false,
    };
    state.sidePanels.scheduleReturnTrip = {
      ...state.sidePanels.scheduleReturnTrip,
      isOpen: false,
    };
    state.sidePanels.quoteTaskDetails = {
      ...state.sidePanels.quoteTaskDetails,
      isOpen: false,
    };
    state.sidePanels.quoteApprovedTaskDetails = {
      ...state.sidePanels.quoteApprovedTaskDetails,
      isOpen: false,
    };
    state.sidePanels.quoteDetails = {
      ...state.sidePanels.quoteDetails,
      isOpen: false,
    };
    state.sidePanels.messages = {
      ...state.sidePanels.messages,
      workOrder: undefined,
      workOrderUuid: undefined,
      isOpen: false,
    };
    state.sidePanels.tripSummary = {
      ...state.sidePanels.tripSummary,
      isOpen: false,
      isLoading: false,
      tripId: undefined,
    };
  },
  openIssueDetailsSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WorkOrderIssueType>,
  ) => {
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      // ORI-593 (QA): `data` + its sibling `options` are derived by the shared
      // buildIssueDetailsPanelSnapshot helper so this open-time snapshot and the
      // getWorkOrderDetails().fulfilled re-sync can never drift apart.
      ...buildIssueDetailsPanelSnapshot(action.payload),
      isOpen: true,
    };
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isClosingWO: false,
    };
  },
  closeIssueDetailsSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      data: undefined,
      isOpen: false,
    };
  },
  updateIssueDetailsQuoteTaskOptions: (
    state: WorkOrderStateType,
    action: PayloadAction<TaskOptionType[]>,
  ) => {
    state.sidePanels.issueDetails.options = action.payload;
  },
  openCloseWorkOrderSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<{ failedCallback?: () => void } | undefined>,
  ) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isClosingWO: true,
    };
    const allMarkedDone = checkAllIssuesMarked(state.source.work_order_issues);
    if (allMarkedDone) {
      const trip: TripType = state.source.trips[0];
      const currentTime: AppDate = dayjs();
      const closeWorkOrderData: CloseWorkOrderSidePanelType = {
        ...state.sidePanels.closeWorkOrder,
        currentTab: WO_CLOSE_WORK_ORDER_TABS_KEYS.SUMMARY,
        data: {
          elapse_time: {
            travel_time_in_sec: trip.total_drive_time_in_sec,
            completed_time_in_sec:
              currentTime.diff(
                trip?.work_resumed_at ?? trip.work_started_at,
                "s",
              ) + (trip.total_labor_time_in_sec || 0),
          },
          // ORI-602: the Review Summary Trip Cost Breakdown must reflect only the trip
          // being closed. Filtering here — the single point that builds the close-WO
          // issue set — keeps a prior-trip issue out of the render (+ Add Material), the
          // Materials / Total / NTE totals, and the close submit payload together,
          // matching the Technician app, which scopes the breakdown to the active trip.
          // PRIMARY rule: keep only issues whose latest trip is the trip being closed.
          // The vendor `status.value` is the trip-pivot status, which quality check does
          // NOT rewrite, so a `missing_info`/`reassign`/unresolved issue left behind on a
          // prior trip keeps `status === 'done'` and would otherwise leak here — its
          // `latest_trip_id` still points at the prior trip. Fail OPEN when
          // `latest_trip_id` is absent so a stale/partial payload never blanks the panel.
          // A QC-passed issue is additionally excluded as the ORI-588 read-only backstop.
          // `checkAllIssuesMarked` above intentionally stays over the full set so the
          // panel-open gate is unaffected.
          issues: state.source.work_order_issues
            .filter(
              (issue) =>
                (!issue.latest_trip_id ||
                  issue.latest_trip_id === trip?.trip_id) &&
                issue.quality_check_status !== QUALITY_CHECK_STATUS.PASSED,
            )
            .flatMap((issue) => ({
              issue_id: issue.issue_id,
              title: issue.title,
              status: issue.status,
              labor_in_cent:
                issue?.quote_task?.selected_option.labor_cost_in_cents || 0,
              // ORI-503: carry the material through untouched (shallow-cloned only
              // to de-alias the Immer draft). The former `cost_in_cents` backfill
              // and the forced `markup_fee_in_cents: 0` / `markup_fee_type: 'fixed'`
              // overwrites destroyed the real markup context, so the close-WO
              // totals silently dropped material markup.
              materials:
                issue.materials?.map((material: MaterialType) => ({
                  ...material,
                })) ?? [],
            })),
          additional_info: {
            additional: false,
            description: "",
          },
          reason: "",
          service_notes: "",
          labor_in_cent: 0,
        },
        isOpen: true,
        isLoading: true,
      };
      state.sidePanels.closeWorkOrder = closeWorkOrderData;
    } else {
      // Note: If there are pending issues, the side panel won't open
      action?.payload?.failedCallback?.();
    }
  },
  closeCloseWorkOrderSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.closeWorkOrder = {
      ...state.sidePanels.closeWorkOrder,
      data: undefined,
      isOpen: false,
    };
  },
  updateCloseWorkOrderSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<CloseWorkOrderFormType>,
  ) => {
    state.sidePanels.closeWorkOrder = {
      ...state.sidePanels.closeWorkOrder,
      data: action.payload,
    };
  },
  updateCloseWorkOrderTabSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WOCloseWorkOrderTabs>,
  ) => {
    state.sidePanels.closeWorkOrder.currentTab = action.payload;
  },
  changeWODetailsTab: (
    state: WorkOrderStateType,
    action: PayloadAction<WODetailsTabs>,
  ) => {
    state.sidePanels.details.currentTab = action.payload;
  },
  updateVendorWorkOrderDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      workOrderId: string;
      data: ValueOf<WorkOrderDataType>;
      key: string;
    }>,
  ) => {
    const updatedWorkOrders = state.data.map((data) => {
      if (data.work_order_id === action?.payload?.workOrderId) {
        return {
          ...data,
          [action?.payload?.key]: action?.payload?.data,
        };
      }
      return data;
    });
    state.data = updatedWorkOrders;
  },
  setIssuesWarning: (
    state: WorkOrderStateType,
    action: PayloadAction<boolean>,
  ) => {
    state.sidePanels.details.showIssuesWarning = action.payload;
  },

  // Reschedule
  updateRescheduleSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<Partial<RescheduleSidePanelType>>,
  ) => {
    state.sidePanels.reschedule = {
      ...state.sidePanels.reschedule,
      ...action.payload,
    };
  },
  backToRescheduleSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.reschedule.isOpen = true;
    state.sidePanels.reschedule.reason = null;
    state.sidePanels.reschedule.currentStep = undefined;
    state.sidePanels.reschedule.scheduleType = undefined;
    state.sidePanels.reschedule.selectedTime = undefined;
    state.sidePanels.reschedule.formData = undefined;
  },
  backToDetailsSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.reschedule.isOpen = false;
    state.sidePanels.reschedule.reason = null;
  },
  toggleCancelWOSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<boolean>,
  ) => {
    state.sidePanels.cancelWO.isOpen = action.payload;
    if (action.payload) {
      state.sidePanels.cancelWO.steps = { key: "", step: "1" };
    }
  },
  updateCancelWOSidePanelStep: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      key: string;
      step: string;
    }>,
  ) => {
    state.sidePanels.cancelWO.steps = {
      key: action.payload.key,
      step: action.payload.step,
    };
  },
  markUnresolveDetailsSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WorkOrderIssueType>,
  ) => {
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      data: action.payload,
      isOpen: true,
      isEdit: false,
    };
  },
  updateWorkOrderIssueStatusDetailsSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      issue_id: string;
      status: StatusType;
      abilities: string[];
    }>,
  ) => {
    const { abilities, issue_id: issueId, status } = action.payload;

    const { issues } = updateWorkOrderIssueStatus(
      state.source.work_order_issues,
      issueId,
      {
        abilities,
        status,
      },
      status,
    );

    state.source = {
      ...state.source,
      work_order_issues: issues,
    };
  },
  updateWorkOrderIssueQualityCheckStatus: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      issue_id: string;
      status: OptionType;
      note_to_provider: string | null;
    }>,
  ) => {
    const {
      issue_id: issueId,
      status,
      note_to_provider: noteToProvider,
    } = action.payload;

    if (!state.source.work_order_issues) return;

    const updatedIssues = updateWorkOrderIssue(
      state.source.work_order_issues,
      issueId,
      {
        status,
        note_to_provider: noteToProvider ?? undefined,
      },
    );

    state.source = {
      ...state.source,
      work_order_issues: updatedIssues,
    };
  },
  /**
   * ORI-580: drops one media from an issue AFTER the server has confirmed its
   * deletion. Both client copies must be patched — `state.source` feeds the
   * read-only media strip above the form (useIssueMedias), and the issueDetails
   * snapshot is written only by openIssueDetailsSidePanel and — since ORI-593 — by the
   * getWorkOrderDetails().fulfilled re-sync, which re-seeds it ONLY while the panel shows a
   * cancelled/unresolved issue. For every other status a refetch still leaves this snapshot
   * stale, so both copies must be patched here. Unknown issue ids and unknown uuids are a no-op.
   */
  removeIssueMedia: (
    state: WorkOrderStateType,
    action: PayloadAction<{ issueId: string; mediaUuid: string }>,
  ) => {
    const { issueId, mediaUuid } = action.payload;

    const issues = state.source?.work_order_issues;
    const currentIssue = issues?.find(
      (issue: WorkOrderIssueType) => issue.issue_id === issueId,
    );

    if (issues && currentIssue) {
      state.source = {
        ...state.source,
        work_order_issues: updateWorkOrderIssue(issues, issueId, {
          media: (currentIssue.media ?? []).filter(
            (media: MediaType) => media?.media_uuid !== mediaUuid,
          ),
        }),
      };
    }

    const snapshot = state.sidePanels.issueDetails.data;
    if (snapshot?.issue_id === issueId) {
      state.sidePanels.issueDetails.data = {
        ...snapshot,
        media: (snapshot.media ?? []).filter(
          (media: MediaType) => media?.media_uuid !== mediaUuid,
        ),
      };
    }
  },
  closemarkUnresolveDetailsSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      data: undefined,
      isOpen: false,
      isEdit: false,
    };
  },
  editmarkUnresolveDetailsSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WorkOrderIssueType>,
  ) => {
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      data: action.payload,
      isOpen: true,
      isEdit: true,
    };
  },
  updateWorkOrderStatusInList: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      workOrderId: string;
      status: any;
      trip?: Partial<TripType>;
    }>,
  ) => {
    const { workOrderId, status, trip } = action.payload;
    const workOrderIndex = state.data.findIndex(
      (workOrder) => workOrder.work_order_id === workOrderId,
    );

    if (workOrderIndex !== -1) {
      const currentWorkOrder = state.data[workOrderIndex];

      // Update the trip info if provided
      if (trip && currentWorkOrder.trips?.length > 0) {
        state.data[workOrderIndex].trips[0] = {
          ...currentWorkOrder.trips[0],
          ...trip,
        };
      }

      // Create a temporary work order object to calculate the correct status
      const tempWorkOrder = {
        ...currentWorkOrder,
        status,
        trips:
          currentWorkOrder.trips?.length > 0 && trip
            ? [
                {
                  ...currentWorkOrder.trips[0],
                  ...trip,
                },
              ]
            : currentWorkOrder.trips,
      };

      // Use the getWorkOrderStatus helper to calculate the correct display status
      state.data[workOrderIndex].status = getWorkOrderStatus(
        tempWorkOrder.status,
        tempWorkOrder.trips,
      );
    }
  },
  editQuoteDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      quoteId: QuoteType["quote_id"];
      data: Partial<QuoteType>;
    }>,
  ) => {
    const { quoteId, data } = action.payload;
    state.source.quotes = state.source.quotes.map((quote) =>
      quote.quote_id === quoteId ? { ...quote, ...data } : quote,
    );
  },
  /**
   * Drops a single task from a quote, keyed on ids rather than on a caller-supplied task array.
   *
   * The caller cannot filter the list itself and pass the result through `editQuoteDetails`: it
   * would have to read `tasks` BEFORE awaiting the removal request, and the vendor channel can land
   * an `updateWorkOrderQuotes` frame on the same quote while that request is in flight. Writing the
   * pre-request list back would then silently discard whatever the broadcast added. Filtering here
   * runs against current state, so nothing can go stale in between.
   */
  removeQuoteTaskFromQuote: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      quoteEstimationRange?: QuoteType["quote_estimation_range"];
      quoteId: QuoteType["quote_id"];
      taskId: QuoteTaskType["quote_task_id"];
    }>,
  ) => {
    const { quoteEstimationRange, quoteId, taskId } = action.payload;
    state.source.quotes = (state.source.quotes ?? []).map(
      (quote: QuoteType) => {
        if (quote.quote_id !== quoteId) {
          return quote;
        }
        return {
          ...quote,
          // Keep the existing range when the response omits one, so a missing field leaves a stale
          // total rather than wiping the quote's estimate.
          quote_estimation_range:
            quoteEstimationRange ?? quote.quote_estimation_range,
          tasks: (quote.tasks ?? []).filter(
            (task: QuoteTaskType) => task.quote_task_id !== taskId,
          ),
        };
      },
    );
  },
  addQuoteTaskDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      issueId?: string;
      noIssue?: boolean;
      onSubmit?: () => void;
      onSuccess?: (
        formData: QuoteTaskFormType,
        res: QuoteTaskResponseType,
      ) => void;
      taskType: TaskType;
      title?: string;
    }>,
  ) => {
    const { issueId, noIssue, onSubmit, onSuccess, taskType, title } =
      action.payload;
    state.sidePanels.quoteTaskDetails = {
      ...state.sidePanels.quoteTaskDetails,
      // Reset BOTH ids the spread would otherwise carry over from the previously opened task. A
      // leaked `taskId` reaches the form as `formData.id` and, once a Remove action exists, would
      // target the wrong task on the server. `canRemove` is reset for the same reason — a stale
      // `true` must never survive into a panel whose opener did not ask for the button.
      canRemove: false,
      taskId: "",
      issueId,
      isOpen: true,
      options: [
        {
          materials: [],
          optionType: null,
          laborCost: null,
          description: "",
          medias: [],
          materialsCost: 0,
          totalCost: 0,
        },
      ],
      mode: "add",
      noIssue,
      onSubmit,
      onSuccess,
      taskType,
      title: title || "",
    };
  },
  editQuoteTaskDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      canRemove?: boolean;
      issueId?: string;
      noIssue?: boolean;
      options: QuoteTaskOptionFormType[];
      onSubmit?: () => void;
      onSuccess?: (
        formData: QuoteTaskFormType,
        res: QuoteTaskResponseType,
      ) => void;
      taskId: string;
      taskType: TaskType;
      title: string;
    }>,
  ) => {
    const {
      canRemove,
      issueId,
      noIssue,
      options,
      onSubmit,
      onSuccess,
      taskId,
      taskType,
      title,
    } = action.payload;
    state.sidePanels.quoteTaskDetails = {
      ...state.sidePanels.quoteTaskDetails,
      // Written unconditionally (like `noIssue`) so an opener that omits it clears any previous
      // `true` rather than inheriting it through the spread.
      canRemove,
      issueId,
      isOpen: true,
      mode: "edit",
      noIssue,
      options,
      onSubmit,
      onSuccess,
      taskId,
      taskType,
      title,
    };
  },
  viewQuoteTaskDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      options: QuoteTaskOptionFormType[];
      taskId: string;
      taskType: TaskType;
      title: string;
    }>,
  ) => {
    const { options, taskId, taskType, title } = action.payload;
    state.sidePanels.quoteTaskDetails = {
      ...state.sidePanels.quoteTaskDetails,
      canRemove: false,
      isOpen: true,
      options,
      mode: "view",
      taskId,
      taskType,
      title,
    };
  },
  viewApprovedQuoteTaskDetails: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      options: QuoteTaskOptionFormType[];
      taskId: string;
      taskType: TaskType;
      title: string;
      issue: WorkOrderIssueType;
    }>,
  ) => {
    const { options, taskId, taskType, title, issue } = action.payload;
    state.sidePanels.quoteApprovedTaskDetails = {
      ...state.sidePanels.quoteApprovedTaskDetails,
      isOpen: true,
      options,
      mode: "view",
      taskId,
      taskType,
      title,
      issue,
    };
  },
  openMessagesSidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<WorkOrderDataType>,
  ) => {
    state.sidePanels.messages = {
      ...state.sidePanels.messages,
      workOrder: action.payload,
      workOrderUuid: action.payload.work_order_id,
      isOpen: true,
      isLoading: true,
    };
  },
  closeMessagesSidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.messages = {
      ...state.sidePanels.messages,
      workOrder: undefined,
      workOrderUuid: undefined,
      isOpen: false,
    };
  },
  /**
   * Updates the unread messages count for a specific work order in the listing.
   * Used by Pusher events when coordinator sends messages to vendor.
   */
  updateWorkOrderUnreadMessagesCount: (
    state: WorkOrderStateType,
    action: PayloadAction<{
      workOrderId: string;
      unreadMessagesCount: number;
    }>,
  ) => {
    const { workOrderId, unreadMessagesCount } = action.payload;
    const index = state.data.findIndex(
      (wo: WorkOrderListingType) => wo.work_order_id === workOrderId,
    );
    if (index !== -1) {
      state.data[index] = {
        ...state.data[index],
        unread_messages_count:
          (state.data[index].unread_messages_count || 0) + unreadMessagesCount,
      };
    }
  },
  /**
   * Clears the unread messages count for a specific work order.
   * Used when the user opens the messages panel.
   */
  clearWorkOrderUnreadMessagesCount: (
    state: WorkOrderStateType,
    action: PayloadAction<string>,
  ) => {
    const workOrderId = action.payload;
    const index = state.data.findIndex(
      (wo: WorkOrderListingType) => wo.work_order_id === workOrderId,
    );
    if (index !== -1) {
      state.data[index] = {
        ...state.data[index],
        unread_messages_count: 0,
      };
    }
  },
  /**
   * Opens the Trip Summary side panel for the provided trip.
   * Used when a vendor taps a trip card inside the work order Trips tab.
   */
  openTripSummarySidePanel: (
    state: WorkOrderStateType,
    action: PayloadAction<{ tripId: TripType["trip_id"] }>,
  ) => {
    state.sidePanels.tripSummary = {
      ...state.sidePanels.tripSummary,
      isOpen: true,
      isLoading: false,
      tripId: action.payload.tripId,
    };
  },
  /**
   * Closes the Trip Summary side panel and clears the selected trip id.
   */
  closeTripSummarySidePanel: (state: WorkOrderStateType) => {
    state.sidePanels.tripSummary = {
      ...state.sidePanels.tripSummary,
      isOpen: false,
      isLoading: false,
      tripId: undefined,
    };
  },
};

export default reducers;
